<template>
  <v-container
    v-if="$can('browse-logistics')"
    fluid
  >
    <v-row
      align="center"
      class="mb-6"
      no-gutters
    >
      <v-col>
        <div class="headline">
          Logistics Panel
        </div>
      </v-col>
      <v-col
        cols="auto"
        class="pr-6"
      >
        <v-switch
          v-model="showLogisticsChecklist"
          label="Show Documentation Logistics"
        />
      </v-col>
      <v-col cols="auto">
        <v-tabs
          v-model="filters.statsYear"
          background-color="transparent"
          class="ma-0"
        >
          <v-tab
            id="all"
            class="text-none"
            href="#all"
          >
            All
          </v-tab>
          <v-tab
            v-for="year in years"
            :key="year"
            :href="'#' + year"
            class="text-none"
          >
            {{ year }}
          </v-tab>
        </v-tabs>
      </v-col>
    </v-row>

    <v-row
      justify="space-between"
      class="mb-12"
      no-gutters
    >
      <v-col
        lg="3"
      >
        <stat-card
          :color="yachtModelSpecs.statusData[yachtModelSpecs.STATUS_PENDING].hexColor"
          :class="{ 'filtered': !!filters.status }"
        >
          <div class="display-1 font-weight-bold mb-1">
            {{ stats.pending.total }}
          </div>
          <div class="subtitle-1">
            Pending Plan
          </div>
          <div class="mt-6">
            <v-btn
              color="grey darken-1"
              text
              class="text-none ma-0"
              :to="{ name: 'yachts', query: { view: 'pending-logistics', delivery_year: statsYear } }"
            >
              See All
              <v-icon
                right
                small
              >
                arrow_forward
              </v-icon>
            </v-btn>
          </div>
        </stat-card>
      </v-col>
      <v-col
        lg="3"
        class="mr-2"
      >
        <stat-card
          :color="yachtModelSpecs.statusData[yachtModelSpecs.STATUS_PLANNED].hexColor"
          class="clickable"
          :class="{ 'filtered': !!filters.status, 'selected': filters.status === 'planned' }"
          @click.native="filters.status = (filters.status === 'planned') ? null : 'planned'"
        >
          <div class="display-1 font-weight-bold mb-1">
            {{ stats.delivery.planned }}
          </div>
          <div class="subtitle-1">
            Planned
          </div>
        </stat-card>
      </v-col>
      <v-col
        lg="3"
      >
        <stat-card
          :color="yachtModelSpecs.statusData[yachtModelSpecs.STATUS_DELIVERY].hexColor"
          class="clickable"
          :class="{ 'filtered': !!filters.status, 'selected': filters.status === 'production_end' }"
          @click.native="filters.status = (filters.status === 'production_end') ? null : 'production_end'"
        >
          <div class="display-1 font-weight-bold mb-1">
            {{ stats.delivery.production }}
          </div>
          <div class="subtitle-1">
            In Production
          </div>
        </stat-card>
      </v-col>
      <v-col
        lg="3"
        class="mr-2"
      >
        <stat-card
          :color="yachtModelSpecs.statusData[yachtModelSpecs.STATUS_DELIVERY].hexColor"
          class="clickable"
          :class="{ 'filtered': !!filters.status, 'selected': filters.status === 'equipment' }"
          @click.native="filters.status = (filters.status === 'equipment') ? null : 'equipment'"
        >
          <div class="display-1 font-weight-bold mb-1">
            {{ stats.delivery.commissioning }}
          </div>
          <div class="subtitle-1">
            Commissioning
          </div>
        </stat-card>
      </v-col>
      <v-col lg="3">
        <stat-card
          :color="yachtModelSpecs.statusData[yachtModelSpecs.STATUS_DELIVERY].hexColor"
          :class="{ 'filtered': !!filters.status, 'selected': filters.status === 'base_delivery' }"
          class="clickable"
          @click.native="filters.status = (filters.status === 'base_delivery' ? null : 'base_delivery')"
        >
          <div class="display-1 font-weight-bold mb-1">
            {{ stats.delivery.delivery }}
          </div>
          <div class="subtitle-1">
            Final Delivery
          </div>
        </stat-card>
      </v-col>
    </v-row>

    <v-data-table
      :headers="headers"
      :items="filteredYachts"
      :loading="yachts.loading"
      hide-default-footer
      disable-pagination
      class="elevation-5"
    >
      <template v-slot:item="{ item: yacht }">
        <logistics-row
          :yacht="yacht"
          :logistics-categories="logisticsCategories"
          :show-logistics-checklist="showLogisticsChecklist"
        />
      </template>
      <template slot="no-data">
        <div class="text-center">
          There are no yachts at the moment.
        </div>
      </template>
    </v-data-table>
  </v-container>
</template>

<script>
  import Yacht from '@/models/Yacht'
  import StatCard from '@/components/business/logistics/StatCard'
  import LogisticsRow from '@/components/business/logistics/LogisticsRow'
  import Document from '@/models/Document'

  export default {
    name: 'LogisticsIndex',
    components: {
      LogisticsRow,
      StatCard
    },
    data() {
      return {
        loadingStats: false,
        stats: {
          pending: {},
          delivery: {}
        },
        filters: {
          statsYear: null,
          status: null
        },
        yachts: {
          data: [],
          loading: false
        },
        years: [
          this.$moment().year(),
          this.$moment().add(1, 'year').year(),
          this.$moment().add(2, 'year').year(),
          this.$moment().add(3, 'year').year()
        ],
        logisticsCategories: [],
        showLogisticsChecklist: false
      }
    },
    computed: {
      statsYear: function () {
        if (!this.filters.statsYear || this.filters.statsYear === 'all') return undefined

        return this.filters.statsYear
      },
      status: function () {
        if (!this.filters.status) return undefined

        return this.filters.status
      },
      headers: function () {
        if (this.showLogisticsChecklist) {
          let headers = [
            { text: 'ID', value: 'id', sortable: true },
            { text: 'Name', value: 'name', sortable: true }
          ]

          for (let category of this.logisticsCategories) {
            headers.push({
              text: category.text, sortable: false
            })
          }

          return headers
        } else {
          return [
            { text: 'ID', value: 'id', sortable: true },
            { text: 'Name', value: 'name', sortable: true },
            { text: 'Hull', value: 'hull_number', sortable: true },
            { text: 'Model', value: 'model.label', sortable: true },
            { text: 'Delivery Year', value: 'delivery_year', sortable: true },
            { text: 'Last Event', value: 'current_step.estimated_at', sortable: true },
            { text: 'Next Event', value: 'next_step.estimated_at', sortable: true },
            { text: 'Destination', value: 'destination_base.name', sortable: false },
            { text: 'Planned arrival', value: 'final_delivery_estimated_end_at', sortable: true }
          ]
        }
      },
      filteredYachts() {
        return this.yachts.data.filter(yacht => {
          if (!this.status) return true

          if (yacht.status === Yacht.STATUS_PLANNED && this.status === 'planned') return true

          if (!yacht.current_step) return false

          return yacht.next_step.type === this.status
        }).filter(yacht => {
          if (!this.statsYear || this.statsYear === 'all') return true

          return Number(yacht.delivery_year) === Number(this.statsYear)
        })
      }
    },
    watch: {
      'filters.statsYear': function () {
        this.getLogisticsStats()
      }
    },
    created() {
      this.yachtModelSpecs = Yacht

      this.getLogisticsStats()
      this.fetchYachts()
      this.getCategories()
    },
    methods: {
      async getLogisticsStats() {
        this.loadingStats = true

        try {
          this.stats = await Yacht.api.getLogisticsStats(this.statsYear)
        } catch (e) {
          throw e
        } finally {
          this.loadingStats = false
        }
      },
      async fetchYachts() {
        this.yachts.loading = true

        try {
          this.yachts.data = await Yacht.api.fetchLogisticsYachts({
            status: [Yacht.STATUS_DELIVERY, Yacht.STATUS_PLANNED]
          })
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
          throw e
        } finally {
          this.yachts.loading = false
        }
      },
      async getCategories() {
        this.logisticsCategories = await Document.api.getLogisticsCategories()
      }
    }
  }
</script>

<style lang="scss">
  .filtered {
    opacity: 0.4;

    &.selected {
      opacity: 1;
    }
  }
</style>
